import { use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Constants,
  useMeeting,
  usePubSub,
  useMediaDevice,
  useWhiteboard,
  useParticipant,
} from "@videosdk.live/react-sdk";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { useSiteContext } from "../context/SiteContext";
import {
  VirtualBackgroundProcessor,
  VideoSDKNoiseSuppressor,
} from "@videosdk.live/videosdk-media-processor-web";
import {
  createCameraVideoTrack,
  createMicrophoneAudioTrack,
} from "@videosdk.live/react-sdk";
import useIsRecording from "./useIsRecording";
import useIsMobile from "./useIsMobile";
import useWindowSize from "./useWindowSize";
import * as meetingServices from "../services/meetingServices";
import pollServices from "../services/pollServices";
import { toast } from "react-toastify";
import { useBreakoutRooms } from "../context/BreakoutRoomsContext";

const normalizePollForClient = (poll) => {
  if (!poll || typeof poll !== "object") {
    return null;
  }

  const votes = Array.isArray(poll.votes)
    ? poll.votes
      .map((vote) => ({
        ...vote,
        optionIndices: Array.isArray(vote?.optionIndices)
          ? vote.optionIndices
          : [],
      }))
      .filter((vote) => vote.participantId)
    : [];

  const skipped = Array.isArray(poll.skipped)
    ? poll.skipped
      .map((entry) => ({
        participantId: entry?.participantId,
        participantName:
          entry?.participantName ||
          entry?.name ||
          entry?.displayName ||
          "Anonymous",
        timestamp: entry?.timestamp || Date.now(),
      }))
      .filter((entry) => entry.participantId)
    : [];

  const options = (poll.options || []).map((option, index) => {
    const resolvedIndex =
      typeof option === "object" &&
        option !== null &&
        typeof option.index === "number"
        ? option.index
        : index;

    if (typeof option === "string") {
      return {
        text: option,
        index: resolvedIndex,
        votes: votes.filter((vote) =>
          vote.optionIndices?.includes(resolvedIndex)
        ).length,
      };
    }

    if (option && typeof option === "object") {
      const resolvedText =
        option.text ||
        option.label ||
        option.value ||
        `Option ${resolvedIndex + 1}`;

      return {
        ...option,
        index: resolvedIndex,
        text: resolvedText,
        votes: votes.filter((vote) =>
          vote.optionIndices?.includes(resolvedIndex)
        ).length,
      };
    }

    const value = option ?? `Option ${resolvedIndex + 1}`;
    return {
      text: String(value),
      index: resolvedIndex,
      votes: votes.filter((vote) => vote.optionIndices?.includes(resolvedIndex))
        .length,
    };
  });

  const sanitizedPoll = {
    ...poll,
    options,
    votes,
    skipped,
    totalVotes: votes.length,
    skippedCount: skipped.length,
    responseCount: votes.length + skipped.length,
  };

  if (!sanitizedPoll.id) {
    sanitizedPoll.id = poll.pollId || poll._id || `${Date.now()}`;
  }

  sanitizedPoll.lastUpdated = Date.now();

  return sanitizedPoll;
};

const applyVoteToPoll = (poll, vote) => {
  if (!poll || !vote) return poll;

  const normalizedVote = {
    ...vote,
    optionIndices: Array.isArray(vote.optionIndices) ? vote.optionIndices : [],
  };

  const existingVotes = Array.isArray(poll.votes) ? [...poll.votes] : [];

  const existingIndex = existingVotes.findIndex(
    (entry) => entry.participantId === normalizedVote.participantId
  );

  if (existingIndex >= 0) {
    existingVotes[existingIndex] = normalizedVote;
  } else {
    existingVotes.push(normalizedVote);
  }

  const filteredSkipped = Array.isArray(poll.skipped)
    ? poll.skipped.filter(
      (entry) => entry.participantId !== normalizedVote.participantId
    )
    : [];

  return normalizePollForClient({
    ...poll,
    votes: existingVotes,
    skipped: filteredSkipped,
  });
};

const applySkipToPoll = (poll, skipEntry) => {
  if (!poll || !skipEntry) return poll;

  const normalizedSkip = {
    participantId: skipEntry.participantId,
    participantName:
      skipEntry.participantName ||
      skipEntry.name ||
      skipEntry.displayName ||
      "Anonymous",
    timestamp: skipEntry.timestamp || Date.now(),
  };

  if (!normalizedSkip.participantId) {
    return poll;
  }

  const filteredVotes = Array.isArray(poll.votes)
    ? poll.votes.filter(
      (entry) => entry.participantId !== normalizedSkip.participantId
    )
    : [];

  const skippedEntries = Array.isArray(poll.skipped) ? [...poll.skipped] : [];

  const existingIndex = skippedEntries.findIndex(
    (entry) => entry.participantId === normalizedSkip.participantId
  );

  if (existingIndex >= 0) {
    skippedEntries[existingIndex] = normalizedSkip;
  } else {
    skippedEntries.push(normalizedSkip);
  }

  return normalizePollForClient({
    ...poll,
    votes: filteredVotes,
    skipped: skippedEntries,
  });
};

export const useTopBar = ({
  setIsMeetingLeft,
  setCustomVideoStream,
  micOn,
  setMicOn,
  webcamOn,
  setWebcamOn,
  customAudioStream,
  setCustomAudioStream,
  customVideoStream,
  startTranscription,
  startTranslation,
  stopTranslation,
  stopTranscription,
  changeTranslationLanguage,
  publishMeetingSettings,
}) => {
  const { startWhiteboard } = useWhiteboard();
  const {
    meetingRole,
    user,
    isAuthenticated,
    meetingSettings,
    setShowElementSection,
    showElementSection,
    selectedLayout,
    setSelectedLayout,
    hideSelfView,
    setHideSelfView,
    showNonVideoParticipants,
    setShowNonVideoParticipants,
    videoResolution,
    handleVideoResolution,
    setMeetingSession,
    translationStatus,
    showPollsModal,
    setShowPollsModal,
    polls,
    setPolls,
    selectedPoll,
    setSelectedPoll,
    loadingPolls,
    setLoadingPolls,
    showPollResultsModal,
    setShowPollResultsModal,
    pollResults,
    setPollResults,
    setShowPollResults,
    meetingSession,
    setMeetingSettings,
    logoutFromMeeting,
  } = useSiteContext();

  const {
    selectedMic,
    setSelectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    setSideBarMode,
    setLeftSideBarMode,
    leftSideBarMode,
    selectedWebcam,
    setSelectedWebcam,
    setUnreadChat,
    unreadChat,
    recentReactions,
    selectedLanguage,
    setSelectedLanguage,
    setTranslationEnabled,
    translationEnabled,
    setTranscriptionEnabled,
    transcriptionEnabled,
    virtualBackgroundOn,
    setVirtualBackgroundOn,
    selectedBackground,
    setSelectedBackground,
    blurEnabled,
    setBlurEnabled,
    approvalSoundEnabled,
    setApprovalSoundEnabled,
    showRecordingConsentModal,
    setShowRecordingConsentModal,
    participantApproveFunc,
    pipMode,
    setPipMode,
    pipClosedByUser,
    setPipClosedByUser,
    restoreScreenSharePipRef,
  } = useMeetingAppContext();

  const isMobile = useIsMobile();
  const windowSize = useWindowSize();
  const isMobileLike =
    (windowSize?.height || 0) > 0 && windowSize.height < 500 ? true : isMobile;

  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;

  const isMobileLandscape = isMobileLike && isLandscapeOrientation;

  const { getMicrophones, getPlaybackDevices, getCameras } = useMediaDevice();
  const videoProcessor = new VirtualBackgroundProcessor();
  const noiseProcessorRef = useRef(null);
  const virtualBackgroundAppliedRef = useRef(false);
  const mMeeting = useMeeting();
  const { localScreenShareOn, localParticipant } = mMeeting;
  const { screenShareStream } = useParticipant(localParticipant?.id || "");
  const screenSharePipVideoRef = useRef(null);
  const [mics, setMics] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const localMicOn = mMeeting?.localMicOn;
  const changeMic = mMeeting?.changeMic;

  const localWebcamOn = mMeeting?.localWebcamOn;
  const changeWebcam = mMeeting?.changeWebcam;

  const [showMore, setShowMore] = useState(false);
  const [showSetting, setShowSetting] = useState(false);
  const [showVirtualBackgroundModal, setShowVirtualBackgroundModal] =
    useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [webcams, setWebcams] = useState([]);
  const [showReactions, setShowReactions] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showCaptionsDropdown, setShowCaptionsDropdown] = useState(false);
  const [showMicDropdown, setShowMicDropdown] = useState(false);
  const [showSpeakerDropdown, setShowSpeakerDropdown] = useState(false);
  const [showVideoDropdown, setShowVideoDropdown] = useState(false);
  const [showLayoutDropdown, setShowLayoutDropdown] = useState(false);
  const [showMobilePanel, setShowMobilePanel] = useState(false);
  const [showCaptionTranslationPanel, setShowCaptionTranslationPanel] =
    useState(false);
  const [aiNoiseRemovalEnabled, setAiNoiseRemovalEnabled] = useState(false);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [isTogglingMic, setIsTogglingMic] = useState(false);
  const [isTogglingWebcam, setIsTogglingWebcam] = useState(false);


  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpIssueText, setHelpIssueText] = useState("");
  const [showProfileConfirmModal, setShowProfileConfirmModal] = useState(false);

  // Live streaming states
  const [showLiveStreamModal, setShowLiveStreamModal] = useState(false);
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [liveStreamPlatforms, setLiveStreamPlatforms] = useState({
    facebook: false,
    youtube: false,
    linkedin: false,
  });
  const [streamingCredentials, setStreamingCredentials] = useState({
    facebook: {
      url: "",
      key: "",
    },
    youtube: {
      url: "",
      backupUrl: "",
      key: "",
    },
    linkedin: {
      url: "",
      key: "",
    },
  });
  const [streamingStartTime, setStreamingStartTime] = useState(null);
  const [streamingElapsedMs, setStreamingElapsedMs] = useState(0);

  const [showAudioInputDropdown, setShowAudioInputDropdown] = useState(false);
  const [showVideoSourceDropdown, setShowVideoSourceDropdown] = useState(false);
  const [blurStrength, setBlurStrength] = useState(50);
  const [showStartRecordingModal, setShowStartRecordingModal] = useState(false);
  const [recordingStartAt, setRecordingStartAt] = useState(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);

  const [participantUnmutePermissions, setParticipantUnmutePermissions] =
    useState(new Map());

  const activePollRef = useRef(null);
  const isRecording = useIsRecording();
  const isRecordingRef = useRef(isRecording);

  const resolvedSessionId =
    meetingSession == null
      ? "Not available"
      : typeof meetingSession === "string"
        ? meetingSession
        : meetingSession.sessionId ||
        meetingSession.id ||
        meetingSession._id ||
        JSON.stringify(meetingSession);

  // PubSub hooks
  const { publish: publishRaiseHand } = usePubSub("RAISE_HAND");
  const { publish: publishReaction } = usePubSub("EMOJI_REACTION");
  const { publish: publishReactionBreakout } = usePubSub("EMOJI_REACTION_BREAKOUT");

  // Get current room for breakout room reactions
  const { currentRoom } = useBreakoutRooms();

  const { publish: publishPoll } = usePubSub("POLL", {
    onMessageReceived: (data) => {
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;

      if (messageData?.type === "launch" && messageData.poll) {
        const normalizedPoll = normalizePollForClient(messageData.poll);
        if (normalizedPoll) {
          activePollRef.current = normalizedPoll;
          setPollResults(normalizedPoll);
          setShowPollResultsModal(false);
        }
      }
    },
  });

  const { publish: publishPollVoteChannel } = usePubSub("POLL_VOTE", {
    onMessageReceived: (data) => {
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      if (!messageData) return;

      const { type, pollId } = messageData;

      const resolveBasePoll = (id) => {
        if (!id) return null;

        if (activePollRef.current && activePollRef.current.id === id) {
          return activePollRef.current;
        }

        if (pollResults && pollResults.id === id) {
          return pollResults;
        }

        return null;
      };

      const broadcastResultsIfNeeded = (poll) => {
        if (poll?.settings?.shareResultsLive) {
          try {
            publishPollVoteChannel(JSON.stringify({
              type: "results_update",
              pollId: poll.id,
              poll,
              timestamp: Date.now(),
            }));
          } catch (error) {
            console.error("Error broadcasting poll results:", error);
          }
        }

        if (
          ["moderator", "co-host"].includes(meetingRole) &&
          poll?.settings?.showResultsAtEnd &&
          poll?.responseCount > 0
        ) {
          setShowPollResultsModal(true);
        }
      };

      if (type === "vote_submitted") {
        if (!pollId) {
          console.error("vote_submitted event missing pollId", messageData);
          return;
        }

        if (!["moderator", "co-host"].includes(meetingRole)) {
          return;
        }

        const incomingVote = messageData.vote || {
          participantId: messageData.participantId,
          participantName: messageData.participantName,
          optionIndices: messageData.optionIndices,
          timestamp: messageData.timestamp,
        };

        let pollForBroadcast = null;

        setPollResults((previousPoll) => {
          const basePoll = resolveBasePoll(pollId) || previousPoll;

          if (!basePoll) {
            console.warn("Poll state not found for vote_submitted", pollId);
            return previousPoll;
          }

          const updatedPoll = applyVoteToPoll(basePoll, incomingVote);
          pollForBroadcast = updatedPoll;
          activePollRef.current = updatedPoll;
          return updatedPoll;
        });

        broadcastResultsIfNeeded(pollForBroadcast);
        return;
      }

      if (type === "vote_skipped") {
        if (!pollId) {
          console.error("vote_skipped event missing pollId", messageData);
          return;
        }

        if (!["moderator", "co-host"].includes(meetingRole)) {
          return;
        }

        const incomingSkip = messageData.skip || {
          participantId: messageData.participantId,
          participantName: messageData.participantName,
          timestamp: messageData.timestamp,
        };

        let pollForBroadcast = null;

        setPollResults((previousPoll) => {
          const basePoll = resolveBasePoll(pollId) || previousPoll;

          if (!basePoll) {
            console.warn("Poll state not found for vote_skipped", pollId);
            return previousPoll;
          }

          const updatedPoll = applySkipToPoll(basePoll, incomingSkip);
          pollForBroadcast = updatedPoll;
          activePollRef.current = updatedPoll;
          return updatedPoll;
        });

        broadcastResultsIfNeeded(pollForBroadcast);
        return;
      }

      if (type === "results_update" && messageData.poll) {
        const normalizedPoll = normalizePollForClient(messageData.poll);
        if (normalizedPoll) {
          activePollRef.current = normalizedPoll;
          setPollResults(normalizedPoll);
          if (
            ["moderator", "co-host"].includes(meetingRole) &&
            normalizedPoll.settings?.showResultsAtEnd &&
            normalizedPoll.responseCount > 0
          ) {
            setShowPollResultsModal(true);
          }
        }
        return;
      }

      if (!type && messageData.poll) {
        const normalizedPoll = normalizePollForClient(messageData.poll);
        if (normalizedPoll) {
          activePollRef.current = normalizedPoll;
          setPollResults(normalizedPoll);
        }
      }
    },
  });

  // Function to restore PIP window
  const restoreScreenSharePip = useCallback(async () => {
    if (!("pictureInPictureEnabled" in document)) return;
    if (!localScreenShareOn || !screenShareStream) return;
    if (document.pictureInPictureElement) return;

    // Check if the track is still active
    if (screenShareStream.track.readyState === "ended") {
      console.error("Screen share track has ended");
      setPipClosedByUser(false);
      return;
    }

    try {
      const video = document.createElement("video");
      screenSharePipVideoRef.current = video;

      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.style.position = "fixed";
      video.style.left = "-9999px";
      video.style.bottom = "-9999px";
      video.style.width = "640px";
      video.style.height = "360px";
      video.setAttribute("playsinline", "true");

      document.body.appendChild(video);

      const track = screenShareStream.track;
      if (!track || track.readyState === "ended") {
        console.error("Screen share track is not available or has ended");
        setPipClosedByUser(false);
        video.remove();
        return;
      }

      const stream = new MediaStream([track]);
      video.srcObject = stream;

      const cleanup = () => {
        setPipMode(false);

        if (localScreenShareOn && screenShareStream) {
          setPipClosedByUser(true);
        }

        if (video.srcObject) {
          video.srcObject = null;
        }
        video.remove();
        screenSharePipVideoRef.current = null;
      };

      video.addEventListener("leavepictureinpicture", cleanup);

      await new Promise((resolve, reject) => {
        if (video.readyState >= 2) {
          resolve();
        } else {
          const timeout = setTimeout(() => {
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("error", onError);
            reject(new Error("Video metadata load timeout"));
          }, 5000);

          const onLoadedMetadata = () => {
            clearTimeout(timeout);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("error", onError);
            resolve();
          };
          const onError = (err) => {
            clearTimeout(timeout);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("error", onError);
            reject(err);
          };
          video.addEventListener("loadedmetadata", onLoadedMetadata, {
            once: true,
          });
          video.addEventListener("error", onError, { once: true });
        }
      });

      // Start playing the video
      await video.play();

      let attempts = 0;
      while (video.readyState < 3 && attempts < 50) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        attempts++;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      await video.requestPictureInPicture();

      setPipMode(true);
      setPipClosedByUser(false);
    } catch (err) {
      console.error("PiP restore error", err);
      setPipClosedByUser(false);
      // Clean up on error
      if (screenSharePipVideoRef.current) {
        screenSharePipVideoRef.current.srcObject = null;
        screenSharePipVideoRef.current.remove();
        screenSharePipVideoRef.current = null;
      }
    }
  }, [localScreenShareOn, screenShareStream, setPipMode, setPipClosedByUser]);

  useEffect(() => {
    if (restoreScreenSharePipRef) {
      restoreScreenSharePipRef.current = restoreScreenSharePip;
    }
  }, [restoreScreenSharePip, restoreScreenSharePipRef]);

  // Automatically manage PiP based on local screen sharing state
  useEffect(() => {
    if (!("pictureInPictureEnabled" in document)) return;

    async function startScreenSharePip() {
      if (!localScreenShareOn || !screenShareStream) return;

      // If already in PiP, do nothing
      if (document.pictureInPictureElement) return;

      const video = document.createElement("video");
      screenSharePipVideoRef.current = video;

      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.style.position = "fixed";
      video.style.left = "-9999px";
      video.style.bottom = "-9999px";

      document.body.appendChild(video);

      const stream = new MediaStream([screenShareStream.track]);
      video.srcObject = stream;

      const cleanup = () => {
        setPipMode(false);
        if (localScreenShareOn && screenShareStream) {
          setPipClosedByUser(true);
        }
        if (video.srcObject) {
          video.srcObject = null;
        }
        video.remove();
        screenSharePipVideoRef.current = null;
      };

      video.addEventListener("leavepictureinpicture", cleanup);

      await new Promise((resolve, reject) => {
        if (video.readyState >= 2) {
          resolve();
        } else {
          const onLoadedMetadata = () => {
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("error", onError);
            resolve();
          };
          const onError = (err) => {
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            video.removeEventListener("error", onError);
            reject(err);
          };
          video.addEventListener("loadedmetadata", onLoadedMetadata);
          video.addEventListener("error", onError);
        }
      });

      try {
        await video.play();
        await video.requestPictureInPicture();
        setPipMode(true);
        setPipClosedByUser(false);
      } catch (err) {
        console.error("PiP error", err);
        cleanup();
      }
    }

    async function stopScreenSharePip() {
      try {
        // Exit PiP if it's active (check if any PiP is active, not just our specific element)
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        }
      } catch (err) {
        console.error("Error exiting PiP", err);
      }

      // Clean up video element and tracks
      if (screenSharePipVideoRef.current) {
        try {
          // Stop all tracks
          if (screenSharePipVideoRef.current.srcObject) {
            screenSharePipVideoRef.current.srcObject
              .getTracks()
              .forEach((t) => {
                if (t.readyState !== "ended") {
                  t.stop();
                }
              });
            screenSharePipVideoRef.current.srcObject = null;
          }

          // Remove video element
          if (screenSharePipVideoRef.current.parentNode) {
            screenSharePipVideoRef.current.remove();
          }

          screenSharePipVideoRef.current = null;
        } catch (err) {
          console.error("Error cleaning up video element", err);
        }
      }

      // Reset state
      setPipMode(false);
      setPipClosedByUser(false);
    }

    if (localScreenShareOn && screenShareStream) {
      startScreenSharePip();
    } else {
      // Screen share stopped → close PiP if it was started by screen share
      stopScreenSharePip();
    }

    // Cleanup on unmount
    return () => {
      if (
        screenSharePipVideoRef.current &&
        screenSharePipVideoRef.current.srcObject
      ) {
        screenSharePipVideoRef.current.srcObject
          .getTracks()
          .forEach((t) => t.stop());
        screenSharePipVideoRef.current.remove();
        screenSharePipVideoRef.current = null;
      }
    };
  }, [localScreenShareOn, screenShareStream, setPipMode, setPipClosedByUser]);

  const { publish: publishRecordingNotice } = usePubSub("RECORDING_NOTICE", {
    onMessageReceived: ({ message, senderId }) => {
      const messageData = typeof message === "string" ? JSON.parse(message) : message;
      console.log(
        "publishRecordingNotice",
        messageData,
        senderId,
        mMeeting?.localParticipant?.id
      );

      if (
        messageData?.type === "started" &&
        senderId != mMeeting?.localParticipant?.id
      ) {
        setRecordingStartAt(messageData.at || Date.now());
        if (senderId != mMeeting?.localParticipant?.id) {
          setShowRecordingConsentModal(true);
        }
      } else if (messageData?.type == "stopped") {
        setShowRecordingConsentModal(false);
      }
    },

    onOldMessagesReceived: (messages) => {
      console.log("onOldMessageReceived", messages);
      if (messages.length > 0) {
        const message = JSON.parse(messages[messages.length - 1].message);
        const senderId = messages[messages.length - 1].senderId;
        console.log(
          "onOldMessageReceived1",
          message,
          mMeeting?.localParticipant?.id
        );

        if (message?.type == "started") {
          setRecordingStartAt(message.at || Date.now());
          if (senderId != mMeeting?.localParticipant?.id) {
            setShowRecordingConsentModal(true);
          }
        } else if (message?.type == "stopped") {
          setShowRecordingConsentModal(false);
        }
      }
    },
  });

  usePubSub("PARTICIPANT_UNMUTE_PERMISSION", {
    onMessageReceived: (data) => {
      console.log("PARTICIPANT_UNMUTE_PERMISSION", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      if (data?.message?.participantId == localParticipantId) {
        setParticipantUnmutePermissions((prev) => {
          if (data?.message?.canUnmute) {
            setMeetingSettings((prev) => ({
              ...prev,
              guestCanUnmute: true,
            }));
          } else {
            setMeetingSettings((prev) => ({
              ...prev,
              guestCanUnmute: false,
            }));
          }
        });
      }
    },
  });

  const { isRequestProcessing } = useMemo(
    () => ({
      isRequestProcessing:
        mMeeting.recordingState ===
        Constants.recordingEvents.RECORDING_STARTING ||
        mMeeting.recordingState ===
        Constants.recordingEvents.RECORDING_STOPPING,
    }),
    [mMeeting.recordingState]
  );

  // Event handlers
  const handleHelpSubmit = async () => {
    if (!helpIssueText.trim()) {
      toast.error("Please share your feedback before submitting.");
      return;
    }

    console.log("Feedback submitted:", {
      meetingId: mMeeting?.meetingId || "N/A",
      meetingSession: resolvedSessionId,
      issue: helpIssueText.trim(),
    });

    await meetingServices.sendFeedback({
      meetingId: mMeeting?.meetingId || "N/A",
      meetingSession: resolvedSessionId,
      issue: helpIssueText.trim(),
    });

    toast.success("Thanks! Your feedback has been captured.");
    setHelpIssueText("");
    setShowHelpModal(false);
  };

  const handleRecordingClick = () => {
    const isRecording = isRecordingRef.current;
    console.log("isRecording", mMeeting.recordingState);
    if (isRecording) {
      mMeeting.stopRecording();
    } else {
      setShowStartRecordingModal(true);
    }
  };

  const handleConfirmStartRecording = () => {
    const at = Date.now();
    if (
      mMeeting.recordingState == Constants.recordingEvents.RECORDING_STARTED ||
      mMeeting.recordingState == Constants.recordingEvents.RECORDING_STARTING
    ) {
      return;
    }
    mMeeting.startRecording();
    try {
      publishRecordingNotice(JSON.stringify({ type: "started", at }), {
        persist: true,
      });
    } catch (_) { }
    setShowStartRecordingModal(false);
  };

  const handleStopRecording = () => {
    mMeeting.stopRecording();
    stopTranscription();
    publishRecordingNotice(JSON.stringify({ type: "stopped" }), {
      persist: true,
    });
  };

  const formatDuration = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const mm = String(Math.floor(total / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const handleStartLiveStream = async (platforms) => {
    try {
      const outputs = [];

      if (platforms.facebook) {
        outputs.push({
          url: streamingCredentials.facebook.url,
          streamKey: streamingCredentials.facebook.key,
        });
      }

      if (platforms.youtube) {
        outputs.push({
          url: streamingCredentials.youtube.url,
          streamKey: streamingCredentials.youtube.key,
        });
      }

      if (platforms.linkedin) {
        outputs.push({
          url: streamingCredentials.linkedin.url,
          streamKey: streamingCredentials.linkedin.key,
        });
      }

      if (outputs.length === 0) {
        alert("Please select at least one platform to stream to.");
        return;
      }

      const liveReponse = await mMeeting.startLivestream(outputs, {
        layout: {
          type: "GRID",
          priority: "SPEAKER",
          gridSize: 4,
        },
        theme: "DARK",
      });

      console.log("liveReponse", liveReponse);

      setIsLiveStreaming(true);
      setLiveStreamPlatforms(platforms);
      setStreamingStartTime(Date.now());
      setShowLiveStreamModal(false);

      console.log("Live streaming started to:", platforms);
    } catch (error) {
      console.error("Error starting live stream:", error);
      alert("Failed to start live stream. Please try again.");
    }
  };

  const handleStopLiveStream = async () => {
    try {
      await mMeeting.stopLivestream();

      setIsLiveStreaming(false);
      setLiveStreamPlatforms({
        facebook: false,
        youtube: false,
        linkedin: false,
      });
      setStreamingStartTime(null);
      setStreamingElapsedMs(0);

      console.log("Live streaming stopped");
    } catch (error) {
      console.error("Error stopping live stream:", error);
      alert("Failed to stop live stream. Please try again.");
    }
  };

  const fetchPolls = async () => {
    setLoadingPolls(true);
    try {
      const response = await pollServices.getUserPolls();
      if (response) {
        if (response.success && response.data) {
          setPolls(Array.isArray(response.data) ? response.data : []);
        } else if (Array.isArray(response)) {
          setPolls(response);
        } else if (response.data && Array.isArray(response.data)) {
          setPolls(response.data);
        } else {
          setPolls([]);
        }
      } else {
        setPolls([]);
      }
    } catch (error) {
      console.error("Error fetching polls:", error);
      setPolls([]);
    } finally {
      setLoadingPolls(false);
    }
  };

  const handleLaunchPoll = () => {
    if (!selectedPoll) {
      toast.error("Please select a poll to launch.");
      return;
    }

    try {
      const pollPayload = normalizePollForClient({
        ...selectedPoll,
        votes: [],
        skipped: [],
        totalVotes: 0,
      });

      if (!pollPayload) {
        toast.error("Unable to launch poll. Poll data is invalid.");
        return;
      }

      setShowPollsModal(false);
      setSelectedPoll(null);
      setShowPollResultsModal(false);
      setPollResults(pollPayload);
      activePollRef.current = pollPayload;
      setShowPollResults(true);
      publishPoll(JSON.stringify({
        type: "launch",
        poll: pollPayload,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error("Error launching poll:", error);
      toast.error("Failed to launch poll. Please try again.");
    }
  };

  const getMics = async () => {
    const mics = await getMicrophones();
    const speakers = await getPlaybackDevices();

    mics && mics?.length && setMics(mics);
    speakers && speakers?.length && setSpeakers(speakers);
  };

  const getWebcams = async () => {
    let webcams = await getCameras();
    webcams && webcams?.length && setWebcams(webcams);
  };

  function onDeviceChanged(devices) {
    getMics();
    const newSpeakerList = devices.devices.filter(
      (device) => device.kind === "audiooutput"
    );

    if (newSpeakerList.length > 0 && !selectedSpeaker?.id) {
      setSelectedSpeaker({
        id: newSpeakerList[0].deviceId,
        label: newSpeakerList[0].label,
      });
    }
  }

  const handleToggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else if (document.documentElement?.requestFullscreen) {
        await document.documentElement.requestFullscreen();
      } else if (document.documentElement?.webkitRequestFullscreen) {
        await document.documentElement.webkitRequestFullscreen();
      } else if (document.documentElement?.msRequestFullscreen) {
        await document.documentElement.msRequestFullscreen();
      } else {
        throw new Error("Fullscreen API not available");
      }
    } catch (_) {
      document.body.classList.toggle("ios-pseudo-fullscreen");
    } finally {
      setIsFullscreen(
        !!document.fullscreenElement ||
        document.body.classList.contains("ios-pseudo-fullscreen")
      );
      setShowMore(false);
    }
  };

  const handleCopyRoomLink = () => {
    try {
      const href = window.location?.href || "";
      navigator.clipboard?.writeText(href).catch(() => { });
    } catch (_) { }
    setShowMore(false);
  };

  const handleToggleTranscription = () => {
    setTranscriptionEnabled((prevEnabled) => {
      const nextEnabled = !prevEnabled;
      if (prevEnabled) {
        if (mMeeting && mMeeting.transcriptionState == Constants.transcriptionEvents.TRANSCRIPTION_STARTED) {
          stopTranscription();
        }
      } else {
        if (mMeeting && mMeeting.transcriptionState == Constants.transcriptionEvents.TRANSCRIPTION_STOPPED) {
          startTranscription();
        }
      }

      return nextEnabled;
    });
  };

  const handleToggleMic = async () => {
    if (isTogglingMic) return; // Prevent multiple clicks
    console.log("micOn", micOn);
    setIsTogglingMic(true);
    try {
      if (localMicOn) {
        // Muting - clean up noise processor and custom stream
        mMeeting.muteMic();
      } else {
        console.log("meetingRole", meetingRole);
        console.log("meetingSettings?.guestCanUnmute", meetingSettings?.guestCanUnmute);
        if (meetingRole != "moderator" && meetingSettings?.guestCanUnmute == false) {
          console.log("You are not allowed to unmute your mic. Please contact the host to unmute your mic.");
          setIsTogglingMic(false);
          toast.error("You are not allowed to unmute your mic. Please contact the host to unmute your mic.");
          return;
        } else {
          mMeeting.unmuteMic();
        }
      }
    } catch (error) {
      console.log("Error toggling mic:", error);
      setIsTogglingMic(false);
    } finally {
      setIsTogglingMic(false);
    }
  };

  const handleToggleWebcam = async (options = {}) => {
    if (isTogglingWebcam) return; // Prevent multiple clicks

    setIsTogglingWebcam(true);
    try {
      if (localWebcamOn) {
        mMeeting.disableWebcam();
      } else {
        // Check if we need to handle video resolution
        if (options.handleVideoResolution && options.mMeeting && options.createCameraVideoTrack && options.videoResolution) {
          if (
            options.mMeeting.participants.size > 1 &&
            options.videoResolution === "high"
          ) {
            await options.handleVideoResolution(
              options.mMeeting,
              options.createCameraVideoTrack,
              "low"
            );
          } else {
            mMeeting.enableWebcam();
          }
        } else {
          mMeeting.enableWebcam();
        }
      }
    } finally {
      setIsTogglingWebcam(false);
    }
  };

  // Handle AI noise removal toggle when mic is already on
  useEffect(() => {
    const applyNoiseRemoval = async () => {
      try {
        const noiseProcessor = new VideoSDKNoiseSuppressor();
        if (aiNoiseRemovalEnabled && mMeeting.micOn) {
          const stream = await createMicrophoneAudioTrack({});
          const processedStream = await noiseProcessor.getNoiseSuppressedAudioStream(
            stream
          );
          changeMic(processedStream);
        } else if (mMeeting.micOn) {
          const stream = await createMicrophoneAudioTrack({});
          changeMic(stream);
        }
      } catch (error) {

      }
    };

    applyNoiseRemoval();
  }, [aiNoiseRemovalEnabled]);

  const handleStartVirtualBackground = async (background, blurEnabledParam) => {
    console.log("videoProcessor", videoProcessor);

    if (!videoProcessor.ready) {
      await videoProcessor.init();
    }

    const isBlurEnabled =
      blurEnabledParam !== undefined ? blurEnabledParam : blurEnabled;

    if (!background && !isBlurEnabled) {
      videoProcessor.stop();

      const stream = await createCameraVideoTrack({});
      changeWebcam(stream);
      setVirtualBackgroundOn(false);
      setSelectedBackground(null);
      return;
    }

    const config = isBlurEnabled
      ? {
        type: "blur",
      }
      : {
        type: "image",
        imageUrl: background,
      };

    const stream = await createCameraVideoTrack({});
    const processedStream = await videoProcessor.start(stream, config);
    console.log("webcamOn", webcamOn);
    if (webcamOn) {
      changeWebcam(processedStream);
      setVirtualBackgroundOn(true);
      setSelectedBackground(background);
    }
  };

  const handleLeaveModal = () => {
    console.log(mMeeting, "mMeeting");
    setShowLeaveModal(true);
    setMeetingSession(null);
    localStorage.setItem("rejoinId", window.location.href);
  };

  useEffect(() => {
    if (logoutFromMeeting) {
      handleLeaveConfirm();
    }
  }, [logoutFromMeeting]);

  const handleLeaveConfirm = () => {
    mMeeting.localParticipant.disableWebcam();
    mMeeting.localParticipant.disableMic();

    if (noiseProcessorRef.current) {
      try {
        if (noiseProcessorRef.current.stopProcessing) {
          noiseProcessorRef.current.stopProcessing();
        }
      } catch (error) {
        console.log("Error stopping noise processor on leave:", error);
      }
      noiseProcessorRef.current = null;
    }

    try {
      if (customVideoStream && customVideoStream.getTracks) {
        customVideoStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      if (customAudioStream && customAudioStream.getTracks) {
        customAudioStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      const videoElements = document.querySelectorAll("video");
      videoElements.forEach((videoElement) => {
        if (videoElement.srcObject instanceof MediaStream) {
          videoElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });

      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audioElement) => {
        if (audioElement.srcObject instanceof MediaStream) {
          audioElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });
    } catch (error) {
      console.error("Error stopping media tracks:", error);
    }

    if (meetingRole === "moderator") {
      try {
        meetingServices.saveMeetingSession(meetingSettings?.roomId);
      } catch (error) {
        console.error("Error saving meeting session:", error);
      }
    }

    setTimeout(() => {
      mMeeting.leave();
      console.log("meetingSettings?.exitUrl", meetingSettings?.exitUrl);
      if (meetingSettings?.exitUrl && meetingSettings.exitUrl != "false") {
        window.location.href = meetingSettings.exitUrl;
      } else {
        setIsMeetingLeft(true);
        setShowLeaveModal(false);
      }
    }, 100);
  };

  const handleEndForAll = () => {
    mMeeting.localParticipant.disableWebcam();
    mMeeting.localParticipant.disableMic();

    if (noiseProcessorRef.current) {
      try {
        if (noiseProcessorRef.current.stopProcessing) {
          noiseProcessorRef.current.stopProcessing();
        }
      } catch (error) {
        console.log("Error stopping noise processor on end:", error);
      }
      noiseProcessorRef.current = null;
    }

    try {
      if (customVideoStream && customVideoStream.getTracks) {
        customVideoStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      if (customAudioStream && customAudioStream.getTracks) {
        customAudioStream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });
      }

      const videoElements = document.querySelectorAll("video");
      videoElements.forEach((videoElement) => {
        if (videoElement.srcObject instanceof MediaStream) {
          videoElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });

      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audioElement) => {
        if (audioElement.srcObject instanceof MediaStream) {
          audioElement.srcObject.getTracks().forEach((track) => {
            if (track.readyState !== "ended") {
              track.stop();
            }
          });
        }
      });
    } catch (error) {
      console.error("Error stopping media tracks:", error);
    }

    if (meetingRole === "moderator") {
      try {
        meetingServices.saveMeetingSession(meetingSettings?.roomId);
      } catch (error) {
        console.error("Error saving meeting session:", error);
      }
    }

    setTimeout(() => {
      mMeeting.end();
      if (meetingSettings?.exitUrl && meetingSettings.exitUrl != "false") {
        window.location.href = meetingSettings.exitUrl;
      } else {
        setIsMeetingLeft(true);
        setShowLeaveModal(false);
      }
    }, 100);
  };

  const handleLeaveCancel = () => {
    setShowLeaveModal(false);
  };

  const handleEmojiClick = ({ char, alt }) => {
    console.log("char", char);
    // Publish to breakout room topic if in a breakout room, otherwise main meeting topic
    if (currentRoom) {
      publishReactionBreakout(JSON.stringify({ char, alt }));
    } else {
      publishReaction(JSON.stringify({ char, alt }));
    }
    setShowReactions(false);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > 50;
    const isDownSwipe = distance < -50;

    if (isUpSwipe) {
      setShowMobilePanel(true);
    } else if (isDownSwipe) {
      setShowMobilePanel(false);
    }
  };

  // Effects


  useEffect(() => {
    if (pollResults) {
      activePollRef.current = pollResults;
    }
  }, [pollResults]);

  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    let intervalId;
    if (isLiveStreaming && streamingStartTime) {
      intervalId = setInterval(() => {
        setStreamingElapsedMs(Date.now() - streamingStartTime);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isLiveStreaming, streamingStartTime]);

  useEffect(() => {
    if (
      mMeeting.recordingState === Constants.recordingEvents.RECORDING_STARTED
    ) {
      if (!recordingStartAt) {
        setRecordingStartAt(Date.now());
        setRecordingElapsedMs(0);
      }
    } else if (
      mMeeting.recordingState === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      setRecordingStartAt(null);
      setRecordingElapsedMs(0);
    }
  }, [mMeeting.recordingState]);

  useEffect(() => {
    let intervalId;
    if (
      mMeeting.recordingState === Constants.recordingEvents.RECORDING_STARTED &&
      recordingStartAt
    ) {
      intervalId = setInterval(() => {
        setRecordingElapsedMs(Date.now() - recordingStartAt);
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [mMeeting.recordingState, recordingStartAt]);

  useEffect(() => {
    if (showPollsModal && meetingRole === "moderator") {
      fetchPolls();
    }
  }, [showPollsModal]);

  useMediaDevice({
    onDeviceChanged,
  });

  useEffect(() => {
    getWebcams();
    getMics();
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(
        !!document.fullscreenElement ||
        document.body.classList.contains("ios-pseudo-fullscreen")
      );
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("msfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("msfullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    console.log("user ", user);
  }, [user]);

  useEffect(() => {
    console.log("blurEnabled changed");
    if (virtualBackgroundOn) {
      handleStartVirtualBackground(selectedBackground, blurEnabled);
    }
  }, [blurEnabled]);

  const meetingIdRef = useRef(null);

  useEffect(() => {
    if (mMeeting?.id && meetingIdRef.current !== mMeeting.id) {
      meetingIdRef.current = mMeeting.id;
      virtualBackgroundAppliedRef.current = false;
    }
  }, [mMeeting?.id]);

  useEffect(() => {
    if (
      virtualBackgroundAppliedRef.current ||
      !mMeeting?.id ||
      !mMeeting.localWebcamOn ||
      !webcamOn ||
      !virtualBackgroundOn ||
      (!selectedBackground && !blurEnabled) ||
      meetingIdRef.current !== mMeeting.id
    ) {
      return;
    }

    const timer = setTimeout(() => {
      if (
        mMeeting?.id &&
        mMeeting.localWebcamOn &&
        webcamOn &&
        virtualBackgroundOn &&
        (selectedBackground || blurEnabled) &&
        !virtualBackgroundAppliedRef.current &&
        meetingIdRef.current === mMeeting.id
      ) {
        virtualBackgroundAppliedRef.current = true;
        handleStartVirtualBackground(selectedBackground, blurEnabled).catch(
          (error) => {
            console.error("Error applying virtual background:", error);
            virtualBackgroundAppliedRef.current = false;
          }
        );
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [
    mMeeting?.id,
    mMeeting?.localWebcamOn,
    webcamOn,
    virtualBackgroundOn,
    selectedBackground,
    blurEnabled,
  ]);

  useEffect(() => {
    return () => {
      if (noiseProcessorRef.current) {
        try {
          if (noiseProcessorRef.current.stopProcessing) {
            noiseProcessorRef.current.stopProcessing();
          }
        } catch (error) {
          console.log("Error stopping noise processor on unmount:", error);
        }
        noiseProcessorRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const triggerButton = event.target.closest(
        'button[aria-label="Mic"], button[aria-label="Speaker"], button[aria-label="Video"], button[aria-label="Layout"], button[aria-label="Tools"], button[aria-label="Captions"], button[aria-label="Settings"], button[aria-label="Reactions"]'
      );
      if (triggerButton) return;

      if (
        showToolsDropdown ||
        showCaptionsDropdown ||
        showMicDropdown ||
        showSpeakerDropdown ||
        showVideoDropdown ||
        showLayoutDropdown ||
        showMore ||
        showReactions ||
        showCaptionTranslationPanel
      ) {
        const toolsDropdown = document.querySelector("[data-tools-dropdown]");
        const captionsDropdown = document.querySelector(
          "[data-captions-dropdown]"
        );
        const micDropdown = document.querySelector("[data-mic-dropdown]");
        const videoDropdown = document.querySelector("[data-video-dropdown]");
        const layoutDropdown = document.querySelector("[data-layout-dropdown]");
        const moreDropdown = document.querySelector("[data-more-dropdown]");
        const reactionsDropdown = document.querySelector(
          "[data-reactions-dropdown]"
        );
        const captionTranslationPanel = document.querySelector(
          "[data-caption-translation-panel]"
        );

        if (
          showToolsDropdown &&
          toolsDropdown &&
          !toolsDropdown.contains(event.target)
        ) {
          setShowToolsDropdown(false);
        }

        if (
          showCaptionsDropdown &&
          captionsDropdown &&
          !captionsDropdown.contains(event.target) &&
          !event.target.closest("[data-ai-captions-button]")
        ) {
          setShowCaptionsDropdown(false);
        }

        if (
          showMicDropdown &&
          micDropdown &&
          !micDropdown.contains(event.target)
        ) {
          setShowMicDropdown(false);
        }

        if (
          showSpeakerDropdown &&
          micDropdown &&
          !micDropdown.contains(event.target)
        ) {
          setShowSpeakerDropdown(false);
        }

        if (
          showVideoDropdown &&
          videoDropdown &&
          !videoDropdown.contains(event.target)
        ) {
          setShowVideoDropdown(false);
        }

        if (
          showLayoutDropdown &&
          layoutDropdown &&
          !layoutDropdown.contains(event.target)
        ) {
          setShowLayoutDropdown(false);
        }

        if (showMore && moreDropdown && !moreDropdown.contains(event.target)) {
          setShowMore(false);
        }

        if (
          showReactions &&
          reactionsDropdown &&
          !reactionsDropdown.contains(event.target)
        ) {
          setShowReactions(false);
        }

        if (
          showCaptionTranslationPanel &&
          captionTranslationPanel &&
          !captionTranslationPanel.contains(event.target)
        ) {
          setShowCaptionTranslationPanel(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    showToolsDropdown,
    showCaptionsDropdown,
    showMicDropdown,
    showSpeakerDropdown,
    showVideoDropdown,
    showLayoutDropdown,
    showMore,
    showReactions,
    showCaptionTranslationPanel,
  ]);

  return {
    // Context values
    meetingRole,
    user,
    isAuthenticated,
    meetingSettings,
    showElementSection,
    setShowElementSection,
    selectedLayout,
    setSelectedLayout,
    hideSelfView,
    setHideSelfView,
    showNonVideoParticipants,
    setShowNonVideoParticipants,
    videoResolution,
    handleVideoResolution,
    translationStatus,
    showPollsModal,
    setShowPollsModal,
    polls,
    selectedPoll,
    setSelectedPoll,
    loadingPolls,
    showPollResultsModal,
    setShowPollResultsModal,
    pollResults,
    meetingSession,
    setMeetingSettings,

    // Meeting app context
    selectedMic,
    setSelectedMic,
    selectedSpeaker,
    setSelectedSpeaker,
    setSideBarMode,
    setLeftSideBarMode,
    leftSideBarMode,
    selectedWebcam,
    setSelectedWebcam,
    setUnreadChat,
    unreadChat,
    recentReactions,
    selectedLanguage,
    setSelectedLanguage,
    setTranslationEnabled,
    translationEnabled,
    setTranscriptionEnabled,
    transcriptionEnabled,
    virtualBackgroundOn,
    approvalSoundEnabled,
    setApprovalSoundEnabled,
    aiNoiseRemovalEnabled,
    setAiNoiseRemovalEnabled,
    showRecordingConsentModal,
    setShowRecordingConsentModal,
    participantApproveFunc,

    // Local state
    isMobile,
    isMobileLike,
    isMobileLandscape,
    mics,
    speakers,
    localMicOn,
    changeMic,
    localWebcamOn,
    changeWebcam,
    showMore,
    setShowMore,
    showSetting,
    setShowSetting,
    showVirtualBackgroundModal,
    setShowVirtualBackgroundModal,
    showLeaveModal,
    setShowLeaveModal,
    webcams,
    showReactions,
    setShowReactions,
    showToolsDropdown,
    setShowToolsDropdown,
    showCaptionsDropdown,
    setShowCaptionsDropdown,
    showMicDropdown,
    setShowMicDropdown,
    showSpeakerDropdown,
    setShowSpeakerDropdown,
    showVideoDropdown,
    setShowVideoDropdown,
    showLayoutDropdown,
    setShowLayoutDropdown,
    showMobilePanel,
    setShowMobilePanel,
    showCaptionTranslationPanel,
    setShowCaptionTranslationPanel,
    isFullscreen,
    setIsFullscreen,
    compactMode,
    setCompactMode,
    showHelpModal,
    setShowHelpModal,
    helpIssueText,
    setHelpIssueText,
    showProfileConfirmModal,
    setShowProfileConfirmModal,

    // Live streaming
    showLiveStreamModal,
    setShowLiveStreamModal,
    isLiveStreaming,
    liveStreamPlatforms,
    setLiveStreamPlatforms,
    streamingCredentials,
    setStreamingCredentials,
    streamingStartTime,
    streamingElapsedMs,

    // Recording
    showAudioInputDropdown,
    setShowAudioInputDropdown,
    showVideoSourceDropdown,
    setShowVideoSourceDropdown,
    blurEnabled,
    setBlurEnabled,
    blurStrength,
    setBlurStrength,
    selectedBackground,
    showStartRecordingModal,
    setShowStartRecordingModal,
    recordingStartAt,
    recordingElapsedMs,
    isRecording,

    // Meeting
    mMeeting,
    startWhiteboard,
    isRequestProcessing,
    resolvedSessionId,

    // PubSub
    publishRaiseHand,
    publishReaction,

    // Handlers
    handleHelpSubmit,
    handleRecordingClick,
    handleConfirmStartRecording,
    handleStopRecording,
    formatDuration,
    handleStartLiveStream,
    handleStopLiveStream,
    handleLaunchPoll,
    getMics,
    getWebcams,
    handleToggleFullscreen,
    handleCopyRoomLink,
    handleToggleTranscription,
    handleToggleMic,
    isTogglingMic,
    handleToggleWebcam,
    isTogglingWebcam,
    handleStartVirtualBackground,
    handleLeaveModal,
    handleLeaveConfirm,
    handleEndForAll,
    handleLeaveCancel,
    handleEmojiClick,
    onTouchStart,
    onTouchMove,
    onTouchEnd,

    // Props passthrough
    startTranscription,
    stopTranscription,
    startTranslation,
    stopTranslation,
    changeTranslationLanguage,
    publishMeetingSettings,
    webcamOn,
    customVideoStream,
    customAudioStream,
    setIsMeetingLeft,
  };
};
